import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, Circle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/card';
import { Badge } from '../UI/badge';
import { Button } from '../UI/button';
import { useLocale } from '../../contexts/LocaleContext';
import { csvImportService } from '../../services/csvImportService';
import { themeClasses } from '../../config/themeClasses';
import { isImportStepSatisfied, NODE_KEY_BY_IMPORT_KEY } from '../../utils/tenantCsvImportStatus';
import { getNodeTabByKey } from '../../config/tenantNodeTabsConfig';
import { cn } from '../../lib/utils';
import CsvDropZone, { spreadsheetValidator } from './CsvDropZone';

const UPLOAD_LABEL_BY_KEY = {
  [csvImportService.CSV_IMPORT_KEYS.tenantCompany]: 'tenantCsvSlotCompany',
  [csvImportService.CSV_IMPORT_KEYS.facilities]: 'tenantCsvSlotFacilities',
  [csvImportService.CSV_IMPORT_KEYS.suppliers]: 'tenantCsvSlotSuppliers',
  [csvImportService.CSV_IMPORT_KEYS.supplierFacilities]: 'tenantCsvSlotSupplierFacilities',
  [csvImportService.CSV_IMPORT_KEYS.commodities]: 'tenantCsvSlotCommodities',
  [csvImportService.CSV_IMPORT_KEYS.geographies]: 'tenantCsvSlotGeographies',
  [csvImportService.CSV_IMPORT_KEYS.logistics]: 'tenantCsvSlotLogistics',
  [csvImportService.CSV_IMPORT_KEYS.products]: 'tenantCsvSlotProducts',
  [csvImportService.CSV_IMPORT_KEYS.graphOverview]: 'tenantCsvSlotGraphOverview',
};

const SPREADSHEET_ACCEPT = '.csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function getExistingSummary(t, importKey, existing) {
  if (!existing) return null;
  if (importKey === csvImportService.CSV_IMPORT_KEYS.tenantCompany && existing.summary) {
    return t('tenantCsvStepExistingCompany', { name: existing.summary });
  }
  if (existing.count > 0) {
    return t('tenantCsvStepExistingCount', { count: existing.count });
  }
  return null;
}

function TenantCsvUploadPanel({
  uploadingByKey,
  resultsByKey,
  existingByKey = {},
  onUpload,
  onOpenDataSection,
}) {
  const { t } = useLocale();
  const [stagedFiles, setStagedFiles] = useState({});

  const steps = useMemo(
    () =>
      csvImportService.CSV_IMPORT_SEQUENCE.map((key, index) => ({
        key,
        step: index + 1,
        labelKey: UPLOAD_LABEL_BY_KEY[key],
      })),
    []
  );

  const firstPendingIndex = useMemo(() => {
    const index = steps.findIndex(
      (step) => !isImportStepSatisfied(step.key, resultsByKey, existingByKey)
    );
    return index === -1 ? steps.length : index;
  }, [steps, resultsByKey, existingByKey]);

  const completedCount = useMemo(
    () =>
      steps.filter((step) => isImportStepSatisfied(step.key, resultsByKey, existingByKey)).length,
    [steps, resultsByKey, existingByKey]
  );

  const handleFile = (stepKey, file) => {
    if (!spreadsheetValidator(file)) {
      toast.error(t('tenantCsvInvalidFileType'));
      return;
    }
    setStagedFiles((prev) => ({ ...prev, [stepKey]: file.name }));
    onUpload(stepKey, file);
  };

  return (
    <Card className={themeClasses.appKit.sectionShell}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{t('tenantCsvStepperTitle')}</CardTitle>
            <CardDescription>{t('tenantCsvStepperDescription')}</CardDescription>
          </div>
          <Badge variant="secondary" className="tabular-nums">
            {t('tenantCsvStepperProgress', { done: completedCount, total: steps.length })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ol className="relative space-y-0">
          {steps.map((step, index) => {
            const result = resultsByKey[step.key];
            const existing = existingByKey[step.key];
            const uploading = Boolean(uploadingByKey[step.key]);
            const hasUploadResult = Boolean(result?.created || result?.updated);
            const hasExisting = Boolean(existing);
            const isComplete = isImportStepSatisfied(step.key, resultsByKey, existingByKey);
            const isCurrent = index === firstPendingIndex && !isComplete;
            const isLast = index === steps.length - 1;
            const stagedName = stagedFiles[step.key];
            const existingSummary = getExistingSummary(t, step.key, existing);
            const nodeKey = NODE_KEY_BY_IMPORT_KEY[step.key];
            const dataPageTab = nodeKey ? getNodeTabByKey(nodeKey)?.pageTab : null;

            let stepStatusLabel = t('tenantCsvStepPending');
            if (hasUploadResult) {
              stepStatusLabel = t('tenantCsvStepComplete');
            } else if (hasExisting) {
              stepStatusLabel = t('tenantCsvStepExisting');
            } else if (isCurrent) {
              stepStatusLabel = t('tenantCsvStepCurrent');
            }

            return (
              <li key={step.key} className="relative flex gap-4 pb-8 last:pb-0">
                {!isLast ? (
                  <span
                    className={cn(
                      'absolute left-4 top-8 w-0.5 -translate-x-1/2',
                      isComplete ? 'bg-success-300' : 'bg-border-light'
                    )}
                    style={{ height: 'calc(100% - 1rem)' }}
                    aria-hidden
                  />
                ) : null}

                <span
                  className={cn(
                    'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold',
                    isComplete && 'border-success-500 bg-success-100 text-success-700',
                    isCurrent && !isComplete && 'border-primary-500 bg-primary-50 text-primary-600',
                    !isComplete && !isCurrent && 'border-border-light bg-background-card text-text-tertiary'
                  )}
                  aria-hidden
                >
                  {isComplete ? <Check className="h-4 w-4" /> : isCurrent ? step.step : <Circle className="h-3 w-3" />}
                </span>

                <div
                  className={cn(
                    'min-w-0 flex-1 rounded-xl border p-4 transition-colors',
                    isComplete && 'border-success-200 bg-success-50/20',
                    isCurrent && !isComplete && 'border-primary-200 bg-primary-50/30',
                    !isComplete && !isCurrent && 'border-border-light bg-background-secondary'
                  )}
                >
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-text-primary">
                        {step.labelKey ? t(step.labelKey) : step.key}
                      </p>
                      <p className="mt-0.5 text-xs text-text-tertiary">{stepStatusLabel}</p>
                      {existingSummary ? (
                        <p className="mt-1 text-xs font-medium text-success-700">{existingSummary}</p>
                      ) : null}
                    </div>
                    {isCurrent ? (
                      <Badge variant="outline" className="border-primary-300 text-primary-700">
                        {t('tenantCsvStepCurrentBadge')}
                      </Badge>
                    ) : null}
                  </div>

                  {hasExisting && !hasUploadResult ? (
                    <p className="mb-3 text-xs text-text-secondary">{t('tenantCsvExistingUploadHint')}</p>
                  ) : null}

                  {step.key === csvImportService.CSV_IMPORT_KEYS.supplierFacilities &&
                  hasExisting &&
                  !hasUploadResult ? (
                    <p className="mb-3 text-xs text-text-secondary">
                      {t('tenantCsvSupplierFacilitiesTabHint')}
                    </p>
                  ) : null}

                  {hasExisting && dataPageTab && onOpenDataSection ? (
                    <div className="mb-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenDataSection(dataPageTab)}
                      >
                        {t('tenantCsvViewInTable')}
                      </Button>
                    </div>
                  ) : null}

                  <CsvDropZone
                    disabled={uploading}
                    fileName={uploading ? t('tenantUploading') : stagedName || null}
                    accept={SPREADSHEET_ACCEPT}
                    validateFile={spreadsheetValidator}
                    onFileSelect={(file) => handleFile(step.key, file)}
                    onClear={() =>
                      setStagedFiles((prev) => {
                        const next = { ...prev };
                        delete next[step.key];
                        return next;
                      })
                    }
                  />

                  {result ? (
                    <div className="mt-3 space-y-1 text-xs text-text-secondary">
                      <p>
                        {t('tenantCsvResultSummary', {
                          created: result.created || 0,
                          updated: result.updated || 0,
                          failed: result.failed || 0,
                        })}
                      </p>
                      {result.watchlistLinked > 0 ? (
                        <p>
                          {t('tenantCsvResultWatchlistLinked', {
                            count: result.watchlistLinked,
                          })}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {result && Array.isArray(result.errors) && result.errors.length > 0 ? (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm font-medium text-text-primary">
                        {t('tenantCsvErrorsTitle')}
                      </summary>
                      <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-text-secondary">
                        {result.errors.slice(0, 20).map((message, errorIndex) => (
                          <li key={`${step.key}-error-${errorIndex}`}>{message}</li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

export default TenantCsvUploadPanel;
